import { Box, Button, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import ClientUsersTable from '../clients/ClientUsersTable';
import ClientUsersToolbar from '../clients/ClientUsersToolbar';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as userSlice from '../../../data/user.slice';
import { fetchUserAccountsAsync } from '../../../data/user.slice';
import { SortModel } from '../../../data/models/sort-model.model';
import AddUserDialog from '../clients/AddUserDialog';
import * as lookupSlice from '../../../data/lookup.slice';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import userService from '../../../services/userService';
import { UserRole } from '../../../data/models/user-role.model';
import PageHeader from '../../controls/PageHeader';
import { UserAccount } from '../../../data/models/user-account.model';

const PREFIX = 'ClientUsers';

export const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  previous: `${PREFIX}-previous`,
  linkText: `${PREFIX}-linkText`,
  toolbar: `${PREFIX}-toolbar`,
  addButton: `${PREFIX}-addButton`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadows[2],
    borderRadius: '3px',
    paddingBottom: theme.spacing(4),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    paddingBottom: 0,
  },

  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.buttonColor.main,
    textDecoration: 'none',
  },

  [`& .${classes.linkText}`]: {
    fontWeight: 600,
    color: 'primary.main',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    padding: theme.spacing(1, 4, 2.5),
    justifyContent: 'space-between',
  },

  [`& .${classes.addButton}`]: {
    minWidth: theme.spacing(18),
  },
}));

export function ClientUsers() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleFilter, setRoleFilter] = useState('0');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAccount | undefined>(
    undefined,
  );

  const users = useAppSelector(userSlice.selectUserAccounts);
  const loading = useAppSelector(userSlice.selectLoadingUserAccounts);

  const userCategoryTypes = useAppSelector(lookupSlice.selectUserCategoryTypes);
  const [userCategoryType, setUserCategoryType] =
    useState<UserCategoryType | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);

  const sortModel: SortModel = {
    column: 'userFirstName',
    direction: 'asc',
  };

  const handleDialogOpen = () => {
    setAddDialogOpen(true);
  };

  const handleDialogClose = () => {
    setAddDialogOpen(false);
    setSelectedUser(undefined);
  };

  const handleEditUser = (user: UserAccount) => {
    setSelectedUser(user);
    handleDialogOpen();
  };

  useEffect(() => {
    return () => {
      dispatch(
        userSlice.setUserAccounts({
          items: [],
          currentPage: 1,
          totalPages: 0,
          itemCount: 0,
        }),
      );
    };
  }, [dispatch]);

  useEffect(() => {
    if (userRoles.length > 0) {
      const roles = userRoles.map((role) => role.userRoleID!);
      const roleIDs = roleFilter === '0' ? roles : [Number(roleFilter)];
      dispatch(
        fetchUserAccountsAsync(
          pageSize,
          page,
          searchText,
          sortModel,
          true,
          roleIDs,
        ),
      );
    }
  }, [dispatch, pageSize, page, searchText, roleFilter, userRoles]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('userCategoryType'));
  }, [dispatch]);

  useEffect(() => {
    const clientCategoryType = userCategoryTypes.find(
      (type) => type.userCategoryTypeBC === 'CLIENT',
    );
    if (clientCategoryType) {
      setUserCategoryType(clientCategoryType);
    }
  }, [userCategoryTypes]);

  useEffect(() => {
    if (userCategoryType) {
      userService
        .getRoles(userCategoryType.userCategoryTypeID)
        .subscribe((roles) => {
          setUserRoles(roles);
        });
    }
  }, [userCategoryType]);

  useEffect(() => {
    setPage(1);
  }, [roleFilter]);

  return (
    <StyledBox className={classes.container}>
      <StyledBox className={classes.header}>
        <PageHeader
          title={t('settings.users')}
          returnLink="/settings"
          returnLinkText={t('navigation.settings')}
        />
      </StyledBox>
      <StyledBox className={classes.toolbar}>
        <ClientUsersToolbar
          searchText={searchText}
          roleFilter={roleFilter}
          userRoles={userRoles}
          onSearchTextChange={setSearchText}
          onRoleChange={setRoleFilter}
        />
        <StyledBox className="right">
          <Button
            onClick={handleDialogOpen}
            variant="contained"
            color="primary"
            size="large"
            className={classes.addButton}>
            {t('common.add')}
          </Button>
        </StyledBox>
      </StyledBox>
      <ClientUsersTable
        users={users}
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onEditUser={handleEditUser}
      />
      <AddUserDialog
        user={selectedUser}
        userRoles={userRoles}
        open={addDialogOpen}
        close={handleDialogClose}
      />
    </StyledBox>
  );
}
